import type { ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { useAuthUser } from '@/auth/useAuthUser';
import { Logo } from '@/layout/Logo';
import { VersionBadge } from '@/layout/VersionBadge';
import { LanguageSwitcher } from '@/layout/LanguageSwitcher';
import { getEnabledModules } from '@/modules/registry';

export function AppLayout(): ReactNode {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = useAuthUser();
  const location = useLocation();

  const enabledModules = user ? getEnabledModules(user.modules, user.roles) : [];

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3 shadow-sm">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3" aria-label="Things360 home">
            <Logo variant="full" size={36} />
          </Link>
          <span className="hidden text-xs text-slate-500 md:inline">{t('app.tagline')}</span>
        </div>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          {user && (
            <div className="text-right text-xs text-slate-600">
              <div className="font-medium text-slate-800">{user.name ?? user.email}</div>
              {user.tenantId && (
                <div className="font-mono text-[10px] text-slate-500" title="tenant_id">
                  {user.tenantId.slice(0, 8)}…
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() =>
              auth.signoutRedirect({ post_logout_redirect_uri: window.location.origin + '/' })
            }
            className="btn-secondary"
          >
            {t('nav.logout')}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <nav className="hidden w-56 shrink-0 border-r border-slate-200 bg-white py-4 md:block">
          <ul className="space-y-1 px-3 text-sm">
            <li>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 ${
                    isActive
                      ? 'bg-brand-50 font-semibold text-brand-600'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                <span className="mr-2">🏠</span>
                {t('nav.dashboard')}
              </NavLink>
            </li>
            {enabledModules.map((m) => (
              <li key={m.id}>
                <NavLink
                  to={m.path}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2 ${
                      isActive
                        ? 'bg-brand-50 font-semibold text-brand-600'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`
                  }
                >
                  <span className="mr-2">{m.icon}</span>
                  {t(m.i18nKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main key={location.pathname} className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      <footer className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-2 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Logo variant="icon" size={16} />
          <span>© {new Date().getFullYear()} Things360 Cloud</span>
        </div>
        <VersionBadge />
      </footer>
    </div>
  );
}
